// import/no-extraneous-dependencies
/* eslint-disable camelcase */
/* eslint jsx-a11y/label-has-associated-control: ["error", { assert: "either", depth: 25 }] */
import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Datepicker from 'react-tailwindcss-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faCalendarAlt, faExclamationTriangle, faMapMarkerAlt, faUser, faFileAlt, faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { useGetOccurrencesQuery } from '../../redux/apiSlice';
import Pagination from '../components/Pagination/Pagination';
import '../components/Pagination/style.css';
import ExportButtons from '../components/CreatePdfExcel/ExportButtons';

const PageSize = 20;

const Occurrences = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [occurrenceType, setOccurrenceType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [occurrenceStatus, setOccurrenceStatus] = useState('');
  const [filteredOccurrences, setFilteredOccurrences] = useState([]);
  const [searchOccurrencePlace, setSearchOccurrencePlace] = useState('');
  console.log(setSearchOccurrencePlace);
  const [value, setValue] = useState({
    startDate: null,
    endDate: null,
  });

  const {
    data: occurrences,
    error,
    isFetching,
    refetch,
  } = useGetOccurrencesQuery();

  // Helper function to get type badge class
  const getTypeBadgeClass = (type) => {
    if (type === 'Incident') return 'bg-red-900 text-red-200';
    if (type === 'Hazard') return 'bg-yellow-900 text-yellow-200';
    if (type === 'Issue') return 'bg-blue-900 text-blue-200';
    return 'bg-gray-900 text-gray-200';
  };

  // Helper function to get status badge class
  const getStatusBadgeClass = (status) => {
    if (status === 'INCOMING') return 'bg-blue-900 text-blue-200';
    if (status === 'INITIATED') return 'bg-yellow-900 text-yellow-200';
    if (status === 'MONITORED') return 'bg-purple-900 text-purple-200';
    if (status === 'CLOSED') return 'bg-green-900 text-green-200';
    return 'bg-gray-900 text-gray-200';
  };
  // pagination, the data to be rendered for a given page
  const paginatedLogs = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * PageSize;
    const lastPageIndex = firstPageIndex + PageSize;
    return filteredOccurrences.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, filteredOccurrences]);

  useEffect(() => {
    if (occurrences) {
      setFilteredOccurrences(occurrences);
    }
  }, [occurrences]);

  useEffect(() => {
    if (occurrences) {
      refetch();
      const query = searchQuery.toLowerCase() || '';

      setFilteredOccurrences(
        occurrences.filter((occurence) => {
          const type = occurence.type?.toLowerCase() || '';
          const number = occurence.number?.toLowerCase() || '';
          const occ = occurence.occurence?.toLowerCase() || '';
          const place = occurence.place?.toLowerCase() || '';
          const date = occurence.date?.toLowerCase() || '';
          const report = occurence.reported_by?.toLowerCase() || '';
          const category = occurence.category?.toLowerCase() || '';
          const status = occurence.status?.toLowerCase() || '';
          return (
            type.includes(query)
            || number.includes(query)
            || occ.includes(query)
            || place.includes(query)
            || report.includes(query)
            || status.includes(query)
            || date.includes(query)
            || category.includes(query)
          );
        }),
      );
    }
  }, [searchQuery, occurrences, refetch]);

  const handleDateValueChange = (newValue) => {
    setValue(newValue);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleFilterChange = (filterType, selectedValue) => {
    if (filterType === 'type') {
      setOccurrenceType(selectedValue);
    } else if (filterType === 'status') {
      setOccurrenceStatus(selectedValue);
    }
  };

  useEffect(() => {
    let filtered = occurrences || [];
    if (occurrenceType) {
      filtered = filtered.filter(
        (occurrence) => occurrence.type === occurrenceType,
      );
    }

    if (occurrenceStatus) {
      filtered = filtered.filter(
        (occurrence) => occurrence.status === occurrenceStatus,
      );
    }
    if (searchOccurrencePlace) {
      // eslint-disable-next-line max-len
      filtered = filtered.filter((occurrence) => occurrence.place
        .toLowerCase()
        .includes(searchOccurrencePlace.toLowerCase()));
    }
    if (value.startDate && value.endDate) {
      const startDate = new Date(value.startDate);
      const endDate = new Date(value.endDate);
      filtered = filtered.filter((log) => {
        const logDate = new Date(log.date);
        return logDate >= startDate && logDate <= endDate;
      });
    }
    setFilteredOccurrences(filtered);
    setCurrentPage(1);
  }, [
    occurrences,
    occurrenceType,
    occurrenceStatus,
    searchOccurrencePlace,
    value,
  ]);

  if (error) {
    console.error('Error fetching occurrences', error);
  }

  if (!occurrences && !isFetching) {
    return (
      <div className="bg-gray-200 text-red-700 bg-opacity-90 lg:p-16 p-8 fixed z-50 w-full h-full top-0 left-0 flex flex-col items-center justify-center">
        <h1>Error while fetching occurrences,please try again later!</h1>
      </div>
    );
  }

  // Define columns for export
  const columns = [
    { headerName: 'No.', field: 'number' },
    { headerName: 'Details', field: 'occurrence' },
    { headerName: 'Place', field: 'place' },
    { headerName: ' Date', field: 'date' },
    { headerName: 'Type', field: 'type' },
    { headerName: 'Category', field: 'category' },
    { headerName: 'Reported by', field: 'reported by' },
    { headerName: 'Status', field: 'status' },
  ];
  return (
    <div className="p-6 w-full min-h-screen bg-gray-900">
      <div className="flex gap-6">
        {/* Main Content */}
        <div className="flex-1 bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
          {/* Header Section */}
          <div className="bg-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-orange-600 rounded-lg">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-white text-lg" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-white">Occurrences Management</h1>
                  <p className="text-gray-300 text-sm">Monitor and manage safety occurrences</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => window.open('https://sms.caa.co.ug/public/report', '_blank')}
                  className="flex items-center space-x-2 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faPlus} className="text-sm" />
                  <span className="font-medium">Report Occurrence</span>
                </button>
                <ExportButtons
                  data={filteredOccurrences}
                  columns={columns}
                  fileName="occurrence-logs"
                />
              </div>
            </div>
          </div>

          {/* Search and Filters Section */}
          <div className="px-6 py-4 bg-gray-750 border-b border-gray-600">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search */}
              <div className="relative">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search occurrences..."
                  value={searchQuery}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-white placeholder-gray-400"
                />
              </div>

              {/* Date Picker */}
              <div className="relative">
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10"
                />
                <Datepicker
                  inputClassName="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-white placeholder-gray-400"
                  showFooter
                  separator="to"
                  value={value}
                  onChange={handleDateValueChange}
                  containerClassName="relative"
                  placeholder="Filter by date range"
                />
              </div>

              {/* Status Filter */}
              <select
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-white"
                value={occurrenceStatus}
                onChange={(event) => handleFilterChange('status', event.target.value)}
              >
                <option value="">All Status</option>
                <option value="INCOMING">Incoming</option>
                <option value="INITIATED">Initiated</option>
                <option value="MONITORED">Monitored</option>
                <option value="CLOSED">Closed</option>
              </select>

              {/* Type Filter */}
              <select
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-white"
                value={occurrenceType}
                onChange={(event) => handleFilterChange('type', event.target.value)}
              >
                <option value="">All Types</option>
                <option value="Incident">Incident</option>
                <option value="Hazard">Hazard</option>
                <option value="Issue">Issue</option>
                <option value="SITREPs">SITREPs</option>
              </select>
            </div>

            {/* Results Count */}
            <div className="mt-4 text-sm text-gray-300">
              {filteredOccurrences.length}
              {' '}
              occurrence
              {filteredOccurrences.length !== 1 ? 's' : ''}
              {' '}
              found
            </div>
          </div>
          {/* Table Section */}
          {filteredOccurrences && filteredOccurrences.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-750">
                  <tr className="border-b border-gray-600">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Number
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Details
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Place
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Reported By
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {paginatedLogs.map(
                    ({
                      id,
                      type,
                      number,
                      date,
                      status,
                      category,
                      occurrence,
                      place,
                      reported_by,
                    }, index) => (
                      <tr key={id} className={`hover:bg-gray-750 ${index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750'}`}>
                        <td className="px-6 py-4">
                          <Link
                            to={`/private/srm/${id}`}
                            className="text-orange-400 hover:text-orange-300 font-medium"
                          >
                            {number}
                          </Link>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-300 max-w-xs truncate">
                            {occurrence}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center space-x-2">
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="text-sm text-gray-400" />
                            <span className="text-sm text-gray-300">{place}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center space-x-2">
                            <FontAwesomeIcon icon={faCalendarAlt} className="text-sm text-gray-400" />
                            <span className="text-sm text-gray-300">{date}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTypeBadgeClass(type)}`}>
                            {type}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-gray-300">{category}</span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center space-x-2">
                            <FontAwesomeIcon icon={faUser} className="text-sm text-gray-400" />
                            <span className="text-sm text-gray-300">{reported_by}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(status)}`}>
                            {status}
                          </span>
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-2xl text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">No occurrences found</h3>
              <p className="text-gray-400 mb-6">Try adjusting your search criteria</p>
            </div>
          )}
          {/* Pagination */}
          {filteredOccurrences && filteredOccurrences.length > 0 && (
            <div className="px-6 py-4 bg-gray-750 border-t border-gray-600">
              <Pagination
                itemsDisplaying={paginatedLogs.length}
                itemsPerPage={PageSize}
                items={filteredOccurrences}
                handleNext={() => setCurrentPage(currentPage + 1)}
                handlePrevious={() => setCurrentPage(currentPage - 1)}
              />
            </div>
          )}
        </div>

        {/* Right Sidebar */}
        <div className="w-80 bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
          {/* Occurrence Statistics */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2 text-orange-400" />
              Occurrence Statistics
            </h3>
            <div className="space-y-4">
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Total Occurrences</span>
                  <span className="text-white font-semibold">{occurrences?.length || 0}</span>
                </div>
              </div>
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">This Month</span>
                  <span className="text-blue-400 font-semibold">
                    {occurrences?.filter((occ) => {
                      const occDate = new Date(occ.date);
                      const currentMonth = new Date().getMonth();
                      const currentYear = new Date().getFullYear();
                      return occDate.getMonth() === currentMonth && occDate.getFullYear()
                      === currentYear;
                    }).length || 0}
                  </span>
                </div>
              </div>
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">This Year</span>
                  <span className="text-green-400 font-semibold">
                    {occurrences?.filter((occ) => {
                      const occDate = new Date(occ.date);
                      const currentYear = new Date().getFullYear();
                      return occDate.getFullYear() === currentYear;
                    }).length || 0}
                  </span>
                </div>
              </div>
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Open Cases</span>
                  <span className="text-yellow-400 font-semibold">
                    {occurrences?.filter((occ) => occ.status !== 'CLOSED').length || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Status Breakdown */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4">Status Breakdown</h3>
            <div className="space-y-3">
              <div className="bg-gray-750 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Incoming</span>
                  <span className="text-blue-400 font-semibold">
                    {occurrences?.filter((occ) => occ.status === 'INCOMING').length || 0}
                  </span>
                </div>
              </div>
              <div className="bg-gray-750 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Initiated</span>
                  <span className="text-yellow-400 font-semibold">
                    {occurrences?.filter((occ) => occ.status === 'INITIATED').length || 0}
                  </span>
                </div>
              </div>
              <div className="bg-gray-750 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Monitored</span>
                  <span className="text-purple-400 font-semibold">
                    {occurrences?.filter((occ) => occ.status === 'MONITORED').length || 0}
                  </span>
                </div>
              </div>
              <div className="bg-gray-750 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Closed</span>
                  <span className="text-green-400 font-semibold">
                    {occurrences?.filter((occ) => occ.status === 'CLOSED').length || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Type Breakdown */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4">Type Breakdown</h3>
            <div className="space-y-3">
              <div className="bg-gray-750 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Incidents</span>
                  <span className="text-red-400 font-semibold">
                    {occurrences?.filter((occ) => occ.type === 'Incident').length || 0}
                  </span>
                </div>
              </div>
              <div className="bg-gray-750 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Hazards</span>
                  <span className="text-yellow-400 font-semibold">
                    {occurrences?.filter((occ) => occ.type === 'Hazard').length || 0}
                  </span>
                </div>
              </div>
              <div className="bg-gray-750 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Issues</span>
                  <span className="text-blue-400 font-semibold">
                    {occurrences?.filter((occ) => occ.type === 'Issue').length || 0}
                  </span>
                </div>
              </div>
              <div className="bg-gray-750 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">SITREPs</span>
                  <span className="text-purple-400 font-semibold">
                    {occurrences?.filter((occ) => occ.type === 'SITREPs').length || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => window.open('https://sms.caa.co.ug/public/report', '_blank')}
                className="w-full bg-orange-600 text-white px-4 py-3 rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center space-x-2"
              >
                <FontAwesomeIcon icon={faPlus} className="text-sm" />
                <span>Report Occurrence</span>
              </button>
              <button
                type="button"
                onClick={() => window.open('https://sms.caa.co.ug/public/report', '_blank')}
                className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center space-x-2"
              >
                <FontAwesomeIcon icon={faFileAlt} className="text-sm" />
                <span>View Reports</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Occurrences;
